import { httpClient as http } from '@/utils/http'

const API_BASE = '/mdm/product-material'

const unwrap = <T>(promise: Promise<unknown>) => promise as Promise<T>

export interface PageRequest {
  pageNum: number
  pageSize: number
}

export interface PageResultVO<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

export interface ProductMaterialSyncStatusVO {
  status: 'normal' | 'error' | 'syncing'
  lastSyncTime?: string
  productCount: number
  materialCount: number
}

export interface ProductPageRequest extends PageRequest {
  searchScheme?: string
  productCode?: string
  productCodeOp?: string
  productName?: string
  productNameOp?: string
  prodLine?: string
  prodLineOp?: string
  brandName?: string
  brandNameOp?: string
  prodCategoryName?: string
  prodCategoryNameOp?: string
  prodTypeName?: string
  prodTypeNameOp?: string
  prodModel?: string
  prodModelOp?: string
  color?: string
  colorOp?: string
  weight?: string
  weightOp?: string
  validFrom?: string
  validFromOp?: string
  validTo?: string
  validToOp?: string
  isActive?: string
  isActiveOp?: string
}

export interface ProductListVO {
  mdmProdMainId: string
  productCode: string
  productName: string
  prodLine?: string
  brandName?: string
  prodCategoryName?: string
  prodTypeName?: string
  prodModel?: string
  color?: string
  weight?: string | number
  isActive?: boolean | number
  validFrom?: string
  validTo?: string
}

export interface ProductDetailVO {
  master?: Record<string, any>
  productInfo?: Record<string, any>
  productDetail?: Record<string, any>
}

export interface MaterialPageRequest extends PageRequest {
  searchScheme?: string
  itemCode?: string
  itemCodeOp?: string
  itemName?: string
  itemNameOp?: string
  itemType?: string
  itemTypeOp?: string
  unit?: string
  unitOp?: string
  materialGroup?: string
  materialGroupOp?: string
  baseUnit?: string
  baseUnitOp?: string
  materialStatus?: string
  materialStatusOp?: string
  isActive?: string
  isActiveOp?: string
}

export interface MaterialListVO {
  mdmItemMainId: string
  itemCode: string
  itemName: string
  itemType?: string
  unit?: string
  materialGroup?: string
  baseUnit?: string
  materialStatus?: string
  isActive?: boolean | number
}

export interface MaterialDetailVO {
  master?: Record<string, any>
  basic?: Record<string, any>
  category?: Record<string, any>
  logistics?: Record<string, any>
  warehouse?: Record<string, any>
}

export const PRODUCT_MATERIAL_SHIPMENT_PRODUCT_LOOKUP_MODE = {
  SKU: 'SKU',
  MATERIAL_NAME: 'MATERIAL_NAME'
} as const

export type ProductMaterialShipmentProductLookupMode =
  (typeof PRODUCT_MATERIAL_SHIPMENT_PRODUCT_LOOKUP_MODE)[keyof typeof PRODUCT_MATERIAL_SHIPMENT_PRODUCT_LOOKUP_MODE]

export interface ProductMaterialShipmentProductLookupRequest {
  keyword: string
  lookupMode: ProductMaterialShipmentProductLookupMode
}

export interface ProductMaterialShipmentProductLookupVO {
  skuCode: string
  productName?: string
  brand?: string
  specialAttributes?: string | null
  barCode?: string | null
  cnDesc?: string
  customsDecl?: string
  enDesc?: string
  boxGauge?: string | null
}

export interface BomQueryRequest {
  mdmItemMainId?: string
  itemCode?: string
  parentItemCode?: string
  childItemCode?: string
  direction?: 'parent' | 'child' | 'both'
}

export interface BomListVO {
  mdmItemBomId: string
  parentItemCode?: string
  parentItemName?: string
  childItemCode?: string
  childItemName?: string
  bomQty?: string | number
  bomUnit?: string
  bomLevel?: number | string
  bomVersion?: string
  parentVersion?: string
  childVersion?: string
  isLatest?: boolean | string
  isActive?: boolean | number
}

export const productMaterialDataApi = {
  syncStatus: () => unwrap<ProductMaterialSyncStatusVO>(http.get(`${API_BASE}/sync-status`)),
  productPage: (params: ProductPageRequest) => unwrap<PageResultVO<ProductListVO>>(http.post(`${API_BASE}/product/page`, params)),
  productDetail: (mdmProdMainId: string) => unwrap<ProductDetailVO>(http.get(`${API_BASE}/product/${mdmProdMainId}`)),
  materialPage: (params: MaterialPageRequest) => unwrap<PageResultVO<MaterialListVO>>(http.post(`${API_BASE}/material/page`, params)),
  materialDetail: (mdmItemMainId: string) => unwrap<MaterialDetailVO>(http.get(`${API_BASE}/material/${mdmItemMainId}`)),
  /**
   * 查询线下发货申请单商品候选。
   *
   * @param params SKU 或物料名称查询条件
   * @returns 当前租户下的商品候选，SKU 双命中时后端优先返回物料数据
   */
  shipmentProductLookup: (params: ProductMaterialShipmentProductLookupRequest) =>
    unwrap<ProductMaterialShipmentProductLookupVO[]>(http.post(`${API_BASE}/shipment-product/list`, params)),
  bomQuery: (params: BomQueryRequest) => unwrap<BomListVO[]>(http.post(`${API_BASE}/bom/query`, params)),
  materialBom: (mdmItemMainId: string) => unwrap<BomListVO[]>(http.get(`${API_BASE}/material/${mdmItemMainId}/bom`))
}
